import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useCompanies } from '../../features/company/CompanyContext';
import { Card, EmptyState, Loading, ModuleHero, Notice, StatCard, StatusPill } from '../../components/ui';
import NoCompany from '../../components/NoCompany';
import { PayButton } from '../../components/PayButton';
import { PAYMENT_STATUS, formatDate, formatDateTime, formatMoney } from '../../lib/format';
import type { Payment } from '../../types/workspace';

type Response = {
  gateway: 'razorpay' | 'sandbox' | 'disabled';
  companyStatus: string;
  payments: Payment[];
};

const KIND: Record<Payment['kind'], { label: string; unlocks: string; to: string }> = {
  RATING_FEE: { label: 'Credit rating fee', unlocks: 'Unlocks your rating result on step 3 · Rated', to: '/rating' },
  SUCCESS_FEE: { label: 'Success fee', unlocks: 'Payable on funds disbursed by a partner', to: '/funded' },
};

export default function Payments() {
  const { data, loading, error, code, reload } = useApi<Response>('/customer/payments');
  const { refresh } = useCompanies();

  if (loading) return <Loading />;
  if (code === 'NO_COMPANY') return <NoCompany />;
  if (error || !data) return <Notice tone="rose">{error}</Notice>;

  const due = data.payments.filter((p) => p.status === 'PENDING' || p.status === 'FAILED');
  const paid = data.payments.filter((p) => p.status === 'PAID');
  const sumBy = (list: Payment[]) =>
    Array.from(list.reduce((m, p) => m.set(p.currency, (m.get(p.currency) || 0) + p.amount), new Map<string, number>()))
      .map(([cur, amt]) => formatMoney(amt, cur))
      .join(' + ') || '—';
  const onPaid = () => {
    void reload(true);
    void refresh();
  };

  return (
    <div>
      <ModuleHero
        eyebrow="Payments"
        title="Payments to Micus"
        description="Pay the rating fee to see your rating result, and the success fee once funds are disbursed. India pays in INR with UPI, cards, netbanking or wallets; other countries pay by international card."
        actions={data.gateway === 'sandbox' ? <StatusPill label="Sandbox mode" tone="amber" /> : <StatusPill label="Secured by Razorpay" tone="green" />}
      />

      {data.gateway === 'disabled' && (
        <div className="mt-5">
          <Notice tone="amber">Online payments are not switched on yet. Please contact your Micus analyst to pay by bank transfer.</Notice>
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-3">
        <StatCard label="Due now" value={sumBy(due)} hint={`${due.length} payment${due.length === 1 ? '' : 's'}`} tone={due.length ? 'amber' : 'slate'} />
        <StatCard label="Paid" value={sumBy(paid)} hint={`${paid.length} payment${paid.length === 1 ? '' : 's'}`} tone="green" />
        <StatCard label="Rating result" value={data.payments.some((p) => p.kind === 'RATING_FEE' && p.status !== 'PAID') ? 'Locked' : 'Unlocked'} hint="rating fee status" to="/rating" />
      </div>

      {data.payments.length === 0 ? (
        <div className="mt-6">
          <EmptyState title="Nothing to pay" body="No Micus fees apply to this company right now." />
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {data.payments.map((p) => {
            const kind = KIND[p.kind];
            const st = PAYMENT_STATUS[p.status];
            const payable = (p.status === 'PENDING' || p.status === 'FAILED') && data.gateway !== 'disabled';
            return (
              <Card key={p.id}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-sm font-bold text-[#6b0030]">{kind.label}</h2>
                      <StatusPill label={st?.label || p.status} tone={st?.tone || 'slate'} />
                      <StatusPill label={p.mode === 'DOMESTIC' ? 'India · all methods' : 'International card'} tone="slate" />
                    </div>
                    <p className="mt-0.5 text-xs text-[#6b3a52]">{p.description}</p>
                    <p className="mt-0.5 text-[11px] text-[#c084a0]">
                      {kind.unlocks} ·{' '}
                      <Link to={kind.to} className="font-semibold text-[#d6006e]">
                        view
                      </Link>
                    </p>
                    {p.failureReason && p.status !== 'PAID' && <p className="mt-1 text-[11px] text-[#be123c]">Last attempt: {p.failureReason}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-black text-[#d6006e]">{formatMoney(p.amount, p.currency)}</p>
                    <p className="text-[11px] text-[#c084a0]">{p.status === 'PAID' ? `Paid ${formatDateTime(p.paidAt)}` : `Raised ${formatDate(p.createdAt)}`}</p>
                    {payable && <PayButton payment={p} onPaid={onPaid} className="mt-2" />}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
